/**
 * ServiceAreaList — multi-column list of served cities / zip codes.
 * No map dependency; useful for SEO and when coverage is text-defined.
 *
 * Registered as: service-area / service-area-list
 */
import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { SectionComponentProps } from "../../registry.js";

gsap.registerPlugin(ScrollTrigger);

interface ServiceAreaListProps {
  headline?: string;
  subheadline?: string;
  /** Array of city / zone names */
  cities?: string[];
  /** Optional footer note, e.g. "Don't see your area? Call us." */
  footnote?: string;
  footnoteTarget?: string;
  footnoteLabel?: string;
}

const DEFAULT_CITIES = [
  "Your City",
  "Nearby Town",
  "Another Area",
  "Surrounding Region",
  "Adjacent County",
  "Metro Area",
];

export function ServiceAreaList({ props }: SectionComponentProps) {
  const {
    headline = "Where We Serve",
    subheadline,
    cities = DEFAULT_CITIES,
    footnote = "Don't see your area? Get in touch — we may still be able to help.",
    footnoteTarget = "#quote",
    footnoteLabel = "Contact Us",
  } = props as ServiceAreaListProps;

  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        "[data-stagger-child]",
        { opacity: 0, y: 14 },
        {
          opacity: 1, y: 0, duration: 0.4, stagger: 0.04, ease: "power2.out",
          scrollTrigger: { trigger: el, start: "top 82%", toggleActions: "play none none none" },
        },
      );
    }, el);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 px-6 bg-muted/30" id="service-area">
      <div className="max-w-4xl mx-auto">
        <div className="mb-10 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">{headline}</h2>
          {subheadline && <p className="text-muted-foreground max-w-xl mx-auto">{subheadline}</p>}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-6 gap-y-3 mb-8">
          {cities.map((city, i) => (
            <div key={i} data-stagger-child className="flex items-center gap-2">
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ background: "var(--color-primary)" }}
                aria-hidden="true"
              />
              <span className="text-sm text-foreground">{city}</span>
            </div>
          ))}
        </div>

        {footnote && (
          <div className="text-center pt-6 border-t border-border">
            <p className="text-sm text-muted-foreground">
              {footnote}{" "}
              {footnoteLabel && (
                <a
                  href={footnoteTarget}
                  className="font-semibold hover:underline"
                  style={{ color: "var(--color-primary)" }}
                >
                  {footnoteLabel}
                </a>
              )}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
